import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, Eye, EyeOff, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { CrownLogo } from "../components/CrownLogo";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import api, { formatApiErrorDetail } from "../lib/api";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [showC, setShowC] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) { toast.error("Passwords do not match."); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      toast.success("Password reset. You can now sign in.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center bg-charcoal px-6">
      <div className="grain pointer-events-none absolute inset-0 opacity-[0.03]" />
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
        className="relative w-full max-w-md rounded-2xl border border-charcoal-border bg-charcoal-surface p-8 md:p-10">
        <div className="mb-8 flex justify-center"><CrownLogo /></div>

        {!token ? (
          <p className="text-center text-sm text-muted-foreground" data-testid="reset-invalid">This reset link is missing its token. Please request a new one.</p>
        ) : done ? (
          <div className="text-center" data-testid="reset-success">
            <CheckCircle2 className="mx-auto mb-4 h-10 w-10 text-gold" />
            <h1 className="font-heading text-3xl text-cream">Password updated</h1>
            <p className="mt-2 text-sm text-muted-foreground">Your password has been reset successfully.</p>
            <Button className="mt-6 rounded-full bg-gold text-charcoal hover:bg-gold/90" onClick={() => navigate("/auth")} data-testid="go-signin-btn">
              Go to sign in
            </Button>
          </div>
        ) : (
          <>
            <h1 className="mb-1 text-center font-heading text-3xl text-cream">Choose a new password</h1>
            <p className="mb-8 text-center text-sm text-muted-foreground">Enter and confirm your new password below.</p>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <Label className="text-cream">New password</Label>
                <div className="relative mt-1.5">
                  <Input data-testid="reset-password-input" type={show ? "text" : "password"} minLength={6} required
                    className="border-charcoal-border bg-charcoal pr-10 text-cream focus-visible:ring-gold"
                    value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" />
                  <button type="button" onClick={() => setShow((s) => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-gold" data-testid="reset-password-toggle">
                    {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <div>
                <Label className="text-cream">Confirm new password</Label>
                <div className="relative mt-1.5">
                  <Input data-testid="reset-confirm-input" type={showC ? "text" : "password"} minLength={6} required
                    className="border-charcoal-border bg-charcoal pr-10 text-cream focus-visible:ring-gold"
                    value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="••••••••" />
                  <button type="button" onClick={() => setShowC((s) => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-gold" data-testid="reset-confirm-toggle">
                    {showC ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <Button type="submit" disabled={loading} className="w-full rounded-full bg-gold py-6 text-base font-500 text-charcoal hover:bg-gold/90" data-testid="reset-submit-btn">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Reset password"}
              </Button>
            </form>
          </>
        )}
      </motion.div>
    </div>
  );
}
